import { Component, type ReactNode, type ErrorInfo } from "react";
import { notifyError } from "common/utils/app-monitoring/notify";

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

/**
 * Application-level error boundary for Design Lab.
 *
 * IMPORTANT: This component MUST render a fallback UI when hasError is true.
 * Without a fallback UI, the ErrorBoundary catches errors but continues rendering
 * the same broken children, which can cause:
 * - Infinite render loops that freeze the entire application
 * - Blocked main thread preventing animations and user interactions
 * - Browser tab becoming unresponsive
 *
 * This is especially critical for errors thrown during Konva's canvas rendering
 * (e.g., InvalidStateError from drawImage with 0-dimension canvas), as these
 * errors occur in requestAnimationFrame loops and will continuously re-throw
 * if the problematic component keeps rendering.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  // eslint-disable-next-line class-methods-use-this
  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    notifyError(`[Design Lab][ErrorBoundary] ${error.message}`, {
      context: { componentStack: errorInfo.componentStack }
    });
  }

  /**
   * Renders fallback UI when an error has been caught, otherwise renders children.
   * The fallback UI breaks the render cycle and prevents infinite loops.
   */
  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full items-center justify-center p-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-red-600">Something went wrong</h2>
            <p className="mt-2 text-gray-600">{this.state.error?.message}</p>
            <button
              data-testid="error-boundary-reload-button"
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
